// ============================================================================
// ClientPicker — shown on the standalone /app/reports/client route (no :id in
// the URL). Lets the trainer pick whose progress report to view; selecting a
// client navigates to the per-client report route. Reuses the live client
// roster (useClients) so it stays in sync across devices.
// ============================================================================
import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  SelectField,
} from "../../components";
import { IconChevronRight } from "../../components/icons";
import { useClients } from "../../hooks/clients";
import { LoadingBlock, ErrorBlock } from "../../app/PageState";

export function ClientPicker() {
  const { t } = useTranslation("clientReport");
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const { data: clients, isLoading, isError, refetch } = useClients();
  const [selected, setSelected] = useState<string>("");

  if (isLoading) return <LoadingBlock label={t("state.loading")} />;
  if (isError) return <ErrorBlock onRetry={() => void refetch()} />;

  const list = clients ?? [];

  if (list.length === 0) {
    return (
      <EmptyState
        title={t("picker.empty")}
        description={t("picker.emptyBlurb")}
        action={
          <Button onClick={() => navigate("/app/clients")}>
            {t("actions.chooseClient")}
          </Button>
        }
      />
    );
  }

  const go = (id: string) => {
    if (id) navigate(`/app/clients/${id}/report`);
  };

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
      className="mx-auto max-w-2xl space-y-6"
    >
      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-kicker text-coral">
          {t("kicker")}
        </p>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tightest text-body">
          {t("picker.title")}
        </h1>
        <p className="mt-1 text-body-soft">{t("picker.subtitle")}</p>
      </div>

      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <SelectField
              label={t("picker.label")}
              placeholder={t("picker.placeholder")}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              options={list.map((c) => ({ value: String(c.id), label: c.name }))}
            />
          </div>
          <Button
            disabled={!selected}
            onClick={() => go(selected)}
            iconRight={<IconChevronRight width={16} height={16} />}
          >
            {t("actions.viewClient")}
          </Button>
        </div>
      </Card>

      {/* Quick tap-list of the roster (mobile-first). */}
      <Card padded={false} className="overflow-hidden">
        <ul className="divide-y divide-line-soft">
          {list.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => go(String(c.id))}
                className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors duration-fast hover:bg-surface-sunken"
              >
                <Avatar name={c.name} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-display text-sm font-bold text-body">
                    {c.name}
                  </span>
                  {c.occupation && (
                    <span className="block truncate text-xs text-body-soft">
                      {c.occupation}
                    </span>
                  )}
                </span>
                <IconChevronRight
                  width={18}
                  height={18}
                  className="shrink-0 text-body-faint"
                />
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </motion.div>
  );
}
